/**
 * VerdictSubmitPage
 * =================
 * Expert submits findings — either for a live-session booking, or for an
 * async review submitted via /get-verified. These findings are read back
 * by Claude via the xpert:// MCP resource (closing the loop), or surface
 * directly on the buyer's review-detail page for async reviews.
 *
 * Routes (BR-011 Tranche 1 — polymorphic):
 *   /expert/verdict/:bookingId          (legacy — live session)
 *   /expert/verdict/review/:reviewId    (new — async review)
 *
 * Auth:  Expert only (ProtectedRoute)
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2,
  ChevronRight,
  Plus,
  Trash2,
  ArrowLeft,
  Loader2,
  AlertTriangle,
  Sparkles,
  Send,
  Brain,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import MarketingNav from "@/components/MarketingNav";
import AttachmentsField from "@/components/AttachmentsField";
import { VERDICT_SCHEMAS, buildSchemaPrefix } from "@/lib/verdictSchemas";
import {
  DEFAULT_PUBLIC_BADGE,
  PUBLIC_BADGE_CONSENT_LABEL,
  PUBLIC_BADGE_CONSENT_HELP,
} from "@/lib/verdictBadge";

// ---------------------------------------------------------------------------
// Confidence selector
// ---------------------------------------------------------------------------

const CONFIDENCE_OPTIONS = [
  { value: "high",   label: "High",   color: "text-brand-green", bg: "bg-brand-green/10 border-emerald-500/40", desc: "I'm confident in my assessment" },
  { value: "medium", label: "Medium", color: "text-amber-400",   bg: "bg-amber-500/10 border-amber-500/40",    desc: "Some uncertainty remains" },
  { value: "low",    label: "Low",    color: "text-amber-600",  bg: "bg-amber-50 border-amber-300",  desc: "Further investigation needed" },
];

function ConfidenceButton({ option, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(option.value)}
      className={`flex flex-col items-start p-3 rounded-sm border text-left transition-all ${
        selected
          ? `${option.bg} border-opacity-100`
          : "border-slateline-200 bg-canvas-card hover:border-slateline-300"
      }`}
    >
      <span className={`font-semibold text-sm ${selected ? option.color : "text-slateline-600"}`}>
        {option.label}
      </span>
      <span className="text-xs text-slateline-400 mt-0.5">{option.desc}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Dynamic list editor (for findings / action items)
// ---------------------------------------------------------------------------

function DynamicList({ label, items, onChange, placeholder, maxItems = 20 }) {
  const addItem = () => {
    if (items.length >= maxItems) {
      toast.error(`Maximum ${maxItems} items.`);
      return;
    }
    onChange([...items, ""]);
  };

  const updateItem = (index, value) => {
    const next = [...items];
    next[index] = value;
    onChange(next);
  };

  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <div>
      <Label className="text-slateline-600 mb-2 block">{label}</Label>
      <div className="space-y-2">
        <AnimatePresence>
          {items.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-center gap-2"
            >
              <span className="text-slateline-300 text-sm w-5 text-right flex-shrink-0">{i + 1}.</span>
              <input
                type="text"
                value={item}
                onChange={(e) => updateItem(i, e.target.value)}
                placeholder={placeholder}
                className="flex-1 bg-canvas-card border border-slateline-200 rounded-sm px-3 py-2 text-sm text-ink placeholder-slate-600 focus:outline-none focus:border-brand-green"
              />
              <button
                type="button"
                onClick={() => removeItem(i)}
                className="p-2 text-slateline-300 hover:text-red-400 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
        {items.length < maxItems && (
          <button
            type="button"
            onClick={addItem}
            className="flex items-center gap-2 text-sm text-brand-green hover:text-brand-greenHover transition-colors py-1"
          >
            <Plus className="w-4 h-4" />
            Add {label.toLowerCase().replace("key ", "").replace(" (optional)", "")}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SchemaFieldRenderer — renders one structured per-category field.
// ---------------------------------------------------------------------------

function SchemaFieldRenderer({ field, value, onChange }) {
  if (field.type === "radio") {
    return (
      <div>
        <Label className="text-slateline-600 mb-2 block">{field.label}</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {field.options.map((opt) => {
            const selected = value === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange(opt.value)}
                className={`px-3 py-2 rounded-sm border text-sm transition-all ${
                  selected
                    ? "bg-brand-green/10 border-brand-green text-brand-green font-medium"
                    : "border-slateline-200 bg-canvas-card text-slateline-600 hover:border-slateline-300"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (field.type === "boolean") {
    const on = !!value;
    return (
      <div className="flex items-center justify-between p-3 bg-canvas-card border border-slateline-200 rounded-sm">
        <span className="text-slateline-600 text-sm font-medium">{field.label}</span>
        <button
          type="button"
          onClick={() => onChange(!on)}
          className={`relative w-11 h-6 rounded-full transition-colors ${
            on ? "bg-brand-green" : "bg-slateline-200"
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
              on ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div>
        <Label className="text-slateline-600 mb-2 block">{field.label}</Label>
        <Textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className="bg-canvas-card border-slateline-200 min-h-[80px] focus:border-brand-green"
        />
      </div>
    );
  }

  if (field.type === "list") {
    const items = Array.isArray(value) && value.length > 0 ? value : [""];
    return (
      <DynamicList
        label={field.label}
        items={items}
        onChange={(next) => onChange(next)}
        placeholder={field.placeholder}
      />
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function VerdictSubmitPage() {
  // BR-011 — polymorphic params. /expert/verdict/:bookingId (legacy live
  // session) or /expert/verdict/review/:reviewId (async review).
  const { bookingId, reviewId } = useParams();
  const mode = reviewId ? "review" : "booking";
  const subjectId = reviewId || bookingId;
  const navigate = useNavigate();

  // Convenience: route helpers so the back-nav + cancel buttons go to the
  // right brief page for whichever flow we're in.
  const briefRoute = mode === "review"
    ? `/expert/brief/review/${subjectId}`
    : `/expert/brief/${subjectId}`;

  // BR-011 Tranche 2 — draft auto-save key. We scope per subject (booking
  // or review) so two parallel reviews don't bleed into each other. The
  // version suffix gives us an easy escape hatch if the shape ever changes.
  const draftKey = `xpert:verdict-draft:v1:${mode}:${subjectId}`;

  // Hydrate from localStorage on first render. We do this synchronously in
  // the useState initializer rather than in a useEffect so the form fills
  // in instantly — no flash of empty fields.
  const loadDraft = () => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem(draftKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Cheap sanity check — if shape doesn't look right, ignore.
      if (typeof parsed !== "object" || parsed === null) return null;
      return parsed;
    } catch {
      return null;
    }
  };
  const draft = loadDraft();

  // Form state — initialized from localStorage draft if present.
  const [verdictText, setVerdictText] = useState(draft?.verdictText || "");
  const [keyFindings, setKeyFindings] = useState(draft?.keyFindings || [""]);
  const [actionItems, setActionItems] = useState(draft?.actionItems || [""]);
  const [confidence, setConfidence] = useState(draft?.confidence || null);
  const [followUp, setFollowUp] = useState(draft?.followUp || false);
  // BR-011 Tranche 2.3 — category-specific structured answers. Keyed by
  // schema-field key (e.g. "risk_level"). Values are whatever shape the
  // field type requires (string for radio, bool for boolean, array for list).
  const [schemaAnswers, setSchemaAnswers] = useState(draft?.schemaAnswers || {});
  // BR-011 Tranche 3.1 — expert-attached files (verdict deliverables like a
  // redlined contract or fixed code file). Same shape as the buyer's
  // submission artifacts: {name, url, content_type, size, ext}.
  const [attachments, setAttachments] = useState(draft?.attachments || []);
  const [draftRestored, setDraftRestored] = useState(!!draft);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  // Async reviews only: whether to publish a public badge. Off by default and
  // deliberately NOT restored from the local draft, so publishing is always a
  // fresh choice on the submit that sends it (backend default is also false).
  const [isPublicBadge, setIsPublicBadge] = useState(DEFAULT_PUBLIC_BADGE);
  // Public one-liner for the badge page. Required when publishing, so the
  // private verdict text is never used as the public summary.
  const [badgeSummary, setBadgeSummary] = useState("");
  const [badgeResult, setBadgeResult] = useState(null);

  // Helper for schema field updates — keeps the state shape stable so we
  // can serialize cleanly.
  const updateSchemaField = (key, value) => {
    setSchemaAnswers((prev) => ({ ...prev, [key]: value }));
  };

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [verdictId, setVerdictId] = useState(null);
  const [existingVerdict, setExistingVerdict] = useState(null);
  const [briefLoading, setBriefLoading] = useState(true);
  const [briefError, setBriefError] = useState(null);
  const [bookingInfo, setBookingInfo] = useState(null);

  // BR-011 Tranche 2 — debounced auto-save to localStorage. We persist
  // any non-empty form state on every change, debounced to avoid hammering
  // localStorage on every keystroke. 800ms is short enough that an expert
  // closing the tab doesn't lose much, long enough that fast typers don't
  // serialize 60×/sec.
  useEffect(() => {
    // If a verdict already exists, the user is in "edit" mode — don't
    // shadow that with a stale localStorage draft. The next person who
    // opens this page should see the saved verdict, not yesterday's WIP.
    if (existingVerdict) return;

    const hasAnyContent =
      verdictText.trim() ||
      keyFindings.some((f) => f.trim()) ||
      actionItems.some((a) => a.trim()) ||
      confidence ||
      followUp ||
      (attachments && attachments.length > 0) ||
      Object.values(schemaAnswers || {}).some((v) => {
        if (Array.isArray(v)) return v.some((x) => x && x.trim());
        if (typeof v === "string") return v.trim();
        return v !== undefined && v !== null && v !== false;
      });

    // If the form is empty, don't save anything — and proactively clear any
    // stale draft so we don't haunt the user with empty state.
    if (!hasAnyContent) {
      try { window.localStorage.removeItem(draftKey); } catch {}
      setDraftSavedAt(null);
      return;
    }

    const timer = setTimeout(() => {
      try {
        window.localStorage.setItem(
          draftKey,
          JSON.stringify({
            verdictText, keyFindings, actionItems, confidence, followUp,
            schemaAnswers, attachments,
            savedAt: new Date().toISOString(),
          })
        );
        setDraftSavedAt(new Date());
      } catch {
        // localStorage can throw on quota / private-mode — silent is fine.
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [draftKey, existingVerdict, verdictText, keyFindings, actionItems, confidence, followUp, schemaAnswers, attachments]);

  // Clear draft helper — used on successful submit and on user-requested
  // discard. Centralized so the two callsites can't drift.
  const clearDraft = () => {
    try { window.localStorage.removeItem(draftKey); } catch {}
    setDraftRestored(false);
    setDraftSavedAt(null);
  };

  const discardDraft = () => {
    if (!window.confirm("Discard your draft and start over?")) return;
    clearDraft();
    setVerdictText("");
    setKeyFindings([""]);
    setActionItems([""]);
    setConfidence(null);
    setFollowUp(false);
    setSchemaAnswers({});
    setAttachments([]);
  };

  // Load brief + pre-fill if verdict exists. Endpoint depends on mode.
  useEffect(() => {
    const load = async () => {
      try {
        const url = mode === "review"
          ? `${API}/verifications/${subjectId}/brief`
          : `${API}/bookings/${subjectId}/brief`;
        const res = await axios.get(url);
        setBookingInfo(res.data);

        // If verdict already submitted, we can pre-fill (allow editing).
        // Pre-fill body retrieval would require another endpoint — skip
        // for now and let expert overwrite.
        if (res.data.verdict_submitted) {
          setExistingVerdict(res.data.verdict_id);
        }
      } catch (err) {
        setBriefError(
          err.response?.data?.detail
            || `Failed to load ${mode === "review" ? "review" : "booking"} info.`
        );
      } finally {
        setBriefLoading(false);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectId, mode]);

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate
    if (!verdictText.trim() || verdictText.trim().length < 10) {
      toast.error("Please write at least a brief verdict summary.");
      return;
    }

    if (mode === "review" && isPublicBadge && !badgeSummary.trim()) {
      toast.error("Add a one-line public summary, or leave the badge private.");
      return;
    }

    // Strip empty items
    const cleanFindings = keyFindings.filter((f) => f.trim());
    const cleanActions  = actionItems.filter((a) => a.trim());

    setSubmitting(true);
    try {
      const url = mode === "review"
        ? `${API}/verifications/${subjectId}/verdict`
        : `${API}/bookings/${subjectId}/verdict`;

      // BR-011 Tranche 2.3 — fold structured per-category answers into a
      // Markdown prefix on verdict_text. Until we add a category_schema_data
      // JSONB column, this keeps the surface single-column while giving the
      // buyer's eventual rendering a clean structured assessment to display.
      const category = bookingInfo?.category;
      const prefix = buildSchemaPrefix(category, schemaAnswers);
      const finalVerdictText = (prefix + verdictText).trim();

      const body = {
        verdict_text:          finalVerdictText,
        confidence_level:      confidence,
        key_findings:          cleanFindings,
        action_items:          cleanActions,
        follow_up_recommended: followUp,
        // BR-011 Tranche 3.1 — expert deliverables (redlined doc, fixed
        // code, etc.). Persisted into expert_verdicts.attachments and
        // surfaced on the buyer's review-detail page.
        attachments:           attachments,
      };
      // Async reviews create a badge; send the publish choice explicitly
      // rather than relying on the backend default. Live-session verdicts
      // (bookings) create no badge, so they don't carry the field.
      if (mode === "review") {
        body.is_public_badge = isPublicBadge;
        body.verdict_summary = isPublicBadge ? badgeSummary.trim() : null;
      }
      const res = await axios.post(url, body);

      setVerdictId(res.data.verdict_id);
      if (mode === "review") {
        setBadgeResult({ slug: res.data.badge_slug, isPublic: res.data.is_public === true });
      }
      setSubmitted(true);
      // Draft has served its purpose — clear so a future visit shows the
      // verdict-submitted state cleanly without leftover scratchpad data.
      clearDraft();
      toast.success(
        mode === "review"
          ? "Verdict submitted! The buyer will be notified."
          : "Verdict submitted! Claude can now relay your findings to the client."
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit verdict.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Loading / error
  // ---------------------------------------------------------------------------

  if (briefLoading) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
      </div>
    );
  }

  if (briefError) {
    return (
      <div className="min-h-screen bg-canvas">
        <MarketingNav />
        <div className="max-w-2xl mx-auto px-4 pt-32 text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-slateline-500 mb-4">{briefError}</p>
          <Button onClick={() => navigate("/expert-dashboard")} variant="outline">Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Success state
  // ---------------------------------------------------------------------------

  if (submitted) {
    return (
      <div className="min-h-screen bg-canvas">
        <MarketingNav />
        <div className="max-w-2xl mx-auto px-4 pt-32 text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="mb-6"
          >
            <div className="w-20 h-20 rounded-full bg-brand-green/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-10 h-10 text-brand-green" />
            </div>
            <h2 className="text-2xl font-bold text-ink mb-2">Verdict Submitted</h2>
            <p className="text-slateline-500 mb-2">
              Your findings are now available to the client's AI assistant.
            </p>
          </motion.div>

          <div className="bg-canvas-card border border-brand-green/20 rounded-sm p-5 mb-8 text-left">
            <div className="flex items-center gap-2 text-brand-green mb-3">
              <Brain className="w-5 h-5" />
              <span className="font-semibold text-sm">How this works</span>
            </div>
            <p className="text-slateline-500 text-sm leading-relaxed">
              {mode === "review" ? (
                <>
                  The buyer has been emailed your verdict and can read it on their
                  review page. Their original AI assistant can also pull your
                  findings via{" "}
                  <code className="text-brand-green bg-canvas px-1 rounded">
                    xpert://verifications/{subjectId}/verdict
                  </code>
                  {" "}— so they don't need to copy-paste anything back.
                  {badgeResult && (
                    <span className="block mt-3">
                      {badgeResult.isPublic && badgeResult.slug ? (
                        <>
                          Public badge:{" "}
                          <a href={`/verified/${badgeResult.slug}`} className="text-brand-green underline">
                            /verified/{badgeResult.slug}
                          </a>
                        </>
                      ) : (
                        "The badge is private. Nothing was published."
                      )}
                    </span>
                  )}
                </>
              ) : (
                <>
                  Claude can now call{" "}
                  <code className="text-brand-green bg-canvas px-1 rounded">resources/read</code>{" "}
                  with{" "}
                  <code className="text-brand-green bg-canvas px-1 rounded">
                    xpert://bookings/{subjectId}/verdict
                  </code>
                  {" "}to pull your findings back into the conversation — so the
                  client doesn't need to copy-paste anything. Your verdict
                  becomes part of the AI's context.
                </>
              )}
            </p>
          </div>

          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => navigate(briefRoute)}
              variant="outline"
              className="border-slateline-200"
            >
              View Brief
            </Button>
            <Button
              onClick={() => navigate("/expert-dashboard")}
              className="bg-brand-green hover:bg-brand-greenHover text-white"
            >
              Back to Dashboard
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Main form
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-canvas">
      <MarketingNav />

      <div className="max-w-2xl mx-auto px-4 pt-28 pb-16">

        {/* Back nav */}
        <button
          onClick={() => navigate(briefRoute)}
          className="flex items-center gap-2 text-slateline-500 hover:text-ink transition-colors mb-8 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Brief
        </button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-brand-green/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-brand-green" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-ink">
                {existingVerdict ? "Update Verdict" : "Submit Verdict"}
              </h1>
              <p className="text-slateline-500 text-sm">
                {mode === "review"
                  ? "Your verdict will be emailed to the buyer and shown on their review page."
                  : "Your findings will be relayed to the client by Claude."}
              </p>
            </div>
          </div>

          {existingVerdict && (
            <div className="mt-3 flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-sm w-fit">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <span className="text-amber-300 text-sm">Updating an existing verdict for this session</span>
            </div>
          )}

          {/* BR-011 Tranche 2 — show a non-intrusive banner if we restored
              a draft from localStorage. Brand green, not amber: this is a
              normal-path event, not an error. */}
          {draftRestored && !existingVerdict && (
            <div className="mt-3 flex items-center gap-3 px-4 py-2 bg-brand-green/10 border border-brand-green/30 rounded-sm w-fit">
              <Sparkles className="w-4 h-4 text-brand-green flex-shrink-0" />
              <span className="text-brand-green text-sm">
                Draft restored from your last visit
              </span>
              <button
                type="button"
                onClick={discardDraft}
                className="text-brand-green text-xs underline hover:text-brand-greenHover"
              >
                Discard
              </button>
            </div>
          )}
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-7">

          {/* Verdict Text */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <Label className="text-slateline-600 mb-2 block">
              Primary Verdict <span className="text-red-400">*</span>
            </Label>
            <p className="text-xs text-slateline-400 mb-2">
              Write your main assessment — this is what Claude will summarise for the client.
              Be specific, actionable, and assume the client has limited domain knowledge.
            </p>
            <Textarea
              value={verdictText}
              onChange={(e) => setVerdictText(e.target.value)}
              placeholder="Based on the information provided, my assessment is..."
              className="bg-canvas-card border-slateline-200 min-h-[160px] focus:border-brand-green"
              required
            />
            <p className="text-xs text-slateline-300 mt-1 text-right">{verdictText.length} chars</p>
          </motion.div>

          {/* Confidence */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Label className="text-slateline-600 mb-2 block">Confidence Level</Label>
            <div className="grid grid-cols-3 gap-2">
              {CONFIDENCE_OPTIONS.map((opt) => (
                <ConfidenceButton
                  key={opt.value}
                  option={opt}
                  selected={confidence === opt.value}
                  onSelect={setConfidence}
                />
              ))}
            </div>
          </motion.div>

          {/* BR-011 Tranche 2.3 — Category-specific structured fields. Renders
              only when we know the category (review or booking) and the
              category has a schema defined. Each field auto-saves with the
              rest of the draft state. */}
          {bookingInfo?.category && VERDICT_SCHEMAS[bookingInfo.category] && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.13 }}
              className="space-y-4 p-5 bg-brand-greenPale/40 border border-brand-green/20 rounded-sm"
            >
              <div className="flex items-center gap-2 text-brand-green">
                <Brain className="w-4 h-4" />
                <h3 className="text-sm font-semibold uppercase tracking-widest">
                  {VERDICT_SCHEMAS[bookingInfo.category].label}
                </h3>
              </div>
              <p className="text-xs text-slateline-500">
                Structured prompts for this category. Your answers fold into
                the verdict so the buyer sees a well-organised assessment.
              </p>
              {VERDICT_SCHEMAS[bookingInfo.category].fields.map((field) => (
                <SchemaFieldRenderer
                  key={field.key}
                  field={field}
                  value={schemaAnswers[field.key]}
                  onChange={(v) => updateSchemaField(field.key, v)}
                />
              ))}
            </motion.div>
          )}

          {/* Key Findings */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <DynamicList
              label="Key Findings (optional)"
              items={keyFindings}
              onChange={setKeyFindings}
              placeholder="e.g. The contract clause 5.2 creates unlimited liability exposure"
            />
          </motion.div>

          {/* Action Items */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <DynamicList
              label="Recommended Action Items (optional)"
              items={actionItems}
              onChange={setActionItems}
              placeholder="e.g. Negotiate clause 5.2 to add a liability cap of $50k"
            />
          </motion.div>

          {/* BR-011 Tranche 3.1 — verdict deliverables (expert attaches a
              redlined contract, fixed code, screenshot annotations, etc).
              Same uploader the buyer uses on /get-verified, reused. */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22 }}
          >
            <AttachmentsField
              attachments={attachments}
              setAttachments={setAttachments}
              label="Verdict deliverables (optional)"
              helperText="Attach the redlined contract, fixed code, annotated screenshots — anything the buyer should download with your verdict. Up to 10 files, 25 MB each."
            />
          </motion.div>

          {/* Follow-up */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="flex items-center justify-between p-4 bg-canvas-card border border-slateline-200 rounded-sm"
          >
            <div>
              <p className="text-slateline-600 text-sm font-medium">Recommend a follow-up review?</p>
              <p className="text-slateline-400 text-xs mt-0.5">Claude will suggest sending a revised version for another written review</p>
            </div>
            <button
              type="button"
              onClick={() => setFollowUp(!followUp)}
              className={`relative w-11 h-6 rounded-full transition-colors ${
                followUp ? "bg-brand-green" : "bg-slateline-200"
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                  followUp ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </motion.div>

          {/* Public badge consent (async reviews only). Same wording as
              ExpertReviewPage, from lib/verdictBadge. Default off. */}
          {mode === "review" && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.27 }}
              className="p-4 bg-canvas-card border border-slateline-200 rounded-sm"
            >
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPublicBadge}
                  onChange={(e) => setIsPublicBadge(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slateline-300 text-brand-green focus:ring-brand-green/30"
                />
                <span className="min-w-0">
                  <span className="block text-slateline-600 text-sm font-medium">
                    {PUBLIC_BADGE_CONSENT_LABEL}
                  </span>
                  <span className="block text-slateline-400 text-xs mt-0.5 leading-relaxed">
                    {PUBLIC_BADGE_CONSENT_HELP}
                  </span>
                </span>
              </label>
              {isPublicBadge && (
                <div className="mt-3">
                  <Label className="text-slateline-600 mb-2 block">
                    Public one-line summary (shown on the badge page)
                  </Label>
                  <input
                    type="text"
                    value={badgeSummary}
                    onChange={(e) => setBadgeSummary(e.target.value)}
                    maxLength={280}
                    placeholder="One line the buyer is happy to have public."
                    className="w-full bg-canvas-card border border-slateline-200 rounded-sm px-3 py-2 text-sm text-ink focus:outline-none focus:border-brand-green"
                  />
                </div>
              )}
            </motion.div>
          )}

          {/* BR-011 Tranche 2 — quiet auto-save indicator. Only shows if a
              draft has actually been persisted this session. Position is
              right-aligned above the buttons so it doesn't fight for
              attention with the primary CTA. */}
          {draftSavedAt && (
            <p className="text-xs text-slateline-400 text-right -mb-3 flex items-center justify-end gap-1">
              <CheckCircle2 className="w-3 h-3 text-brand-green" />
              Draft auto-saved
              <span className="text-slateline-300">
                · {draftSavedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            </p>
          )}

          {/* Submit */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex gap-3 pt-2"
          >
            <Button
              type="submit"
              disabled={submitting || !verdictText.trim()}
              className="bg-brand-green hover:bg-brand-greenHover text-white font-semibold flex-1"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {existingVerdict ? "Update Verdict" : "Submit Verdict"}
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(briefRoute)}
              className="border-slateline-200"
            >
              Cancel
            </Button>
          </motion.div>
        </form>
      </div>
    </div>
  );
}
